/**
 * Document-tab export helpers (#341): flatten a subtree, build an
 * Excel-compatible CSV (UTF-8 BOM), and build a print-optimised HTML page for
 * the "Export to PDF" path (browser print → save as PDF; deliberately no
 * heavy PDF library).
 *
 * All user content is escaped/plain-texted — never interpolated as HTML
 * (rendering untrusted HTML was a prior XSS issue, ISSUES_LOG B4).
 *
 * **F-53 / REQ-6114 + F-21 / REQ-6107 (#585/#581) — these two are export surfaces.**
 * They are surfaces 12a and 12b of the eleven-plus-two the approval record must reach.
 * `api.py::export_item_json` annotates the payload these render from and says so:
 * *"This payload is ALSO what the frontend's CSV and PDF (print) exports render from,
 * so annotating here carries approval into those two client-side formats without a
 * second implementation that could disagree."* The payload did carry `node.approval`;
 * neither renderer read it, so two of the thirteen export surfaces silently opted out
 * of a guarantee the backend asserted on their behalf — a CSV handed to the same
 * auditor as the xlsx disagreed with it by omission.
 *
 * The projection below is deliberately a TRANSCRIPTION of
 * `core/requirements/approval_export.py`, not a re-derivation: same field labels, same
 * order, same value strings, same blank-vs-"Unknown" rules, all read off the record the
 * backend already computed (`node.approval`). Nothing here decides anything about
 * approval state; if a rule changes it changes in the Python and this file follows it.
 * `approvalFields` is exported so both renderers below use ONE copy of it — the
 * failure mode this whole area exists to prevent is two renderers of the same fact.
 */
import { htmlToText } from './htmlText';
import { resolveIdDisplay } from './itemId';

/**
 * The approval columns every export carries, in order. Mirrors
 * `approval_export.APPROVAL_FIELD_LABELS`; the CSV places them exactly where the
 * backend xlsx places them (after Description, before the attribute columns) so the
 * two spreadsheet formats of the same subtree line up column for column.
 */
export const APPROVAL_COLUMNS = [
  'Approval State',
  'Approval Reviewers',
  'Approval Date',
  'Approved At Version',
  'Self-Approved',
  'Reviewer Inactive',
  'Identity Pseudonymised',
];

/** `approval_export._UNKNOWN` — "we did not look" is not "nobody has approved". */
const NOT_EXPORTED = 'not exported';

/**
 * The verification-EVIDENCE columns every export carries, in order. Mirrors
 * `verification_export.VERIFICATION_FIELD_LABELS` (#678 v2), placed right after the
 * approval columns — the same position `xlsx_export`/`docx_export` put them in.
 *
 * #696 sweep: this file's own header comment records that these two renderers
 * silently missed the APPROVAL annotator once already (#585/#581) until the tests
 * below were written. The verification-evidence annotator (#678 v2) landed after
 * that fix and was never wired into either renderer here — the exact same class of
 * gap recurring on the next field the backend added. `node.verification_evidence`
 * is stamped on the same `/export/json` payload these two read (api.py::
 * export_item_json / iam_api.py::_load_project_export_root), so the data was always
 * there; nothing here read it.
 */
export const VERIFICATION_COLUMNS = [
  'Verification Records',
  'Verification Result',
  'Verification Stale',
];

/** `verification_export._fmt_records` — one evidence record per auditable clause. */
function formatVerificationRecords(records) {
  return (records || []).map((r) => {
    const who = r.actor || r.actor_id || 'unknown';
    const ev = r.evidence_ref ? ` (evidence: ${r.evidence_ref})` : '';
    const marks = r.stale ? ' [stale]' : '';
    return `${r.method}: ${r.result}${ev} by ${who} on ${r.created_at || ''}${marks}`;
  }).join('; ');
}

/**
 * `{label: value}` for `VERIFICATION_COLUMNS`, for ONE node — mirrors
 * `verification_export.verification_fields` exactly (records → one string per clause,
 * tri-state result/stale, `not exported` for an un-annotated node — never a blank,
 * which would read as "no evidence" rather than "we did not look").
 */
export function verificationFields(node) {
  const record = node && node.verification_evidence;
  if (!record || typeof record !== 'object') {
    return Object.fromEntries(VERIFICATION_COLUMNS.map((label) => [label, NOT_EXPORTED]));
  }
  const records = record.records || [];
  const evidence = record.evidence || {};
  // `effective_state` (v1 trace-derived state widened by evidence) is the ONLY
  // source for the result column — same contract as the Python projection since
  // #753 — so this client-side CSV/print export agrees with the server exports and
  // REST/MCP by construction. The `/export/json` payloads this reads always carry
  // it (their handlers annotate with links); a payload WITHOUT the key came from a
  // non-annotating source, so render NOT_EXPORTED ("we did not look") rather than
  // re-deriving a weaker evidence-only answer — that derivation disagreed with
  // every other surface for a trace-link-only-verified item.
  let result;
  if (!('effective_state' in record)) result = NOT_EXPORTED;
  else if (record.effective_state === 'verified') result = 'verified';
  else if (record.effective_state === null) result = 'not applicable';
  else result = records.length ? 'not verified' : 'no evidence';
  const stale = records.length ? (evidence.evidence_stale ? 'Yes' : 'No') : 'Unknown';
  return {
    'Verification Records': formatVerificationRecords(records),
    'Verification Result': result,
    'Verification Stale': stale,
  };
}

/** `approval_export._APPROVED_ROLLUP_STATES` — an approval over objection is an approval. */
const APPROVED_ROLLUP_STATES = ['approved', 'approved_over_objection'];

/** `approval_export.format_tristate` — never blank for the third case; a blank reads as "no". */
function formatTristate(value) {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return 'Unknown';
}

/**
 * `approval_export.format_identity_erased` — N-05 / REQ-6127 (#602). The reviewer's
 * display name was replaced by a stable pseudonym under a data-protection erasure. The
 * decision, its timestamp and its place in the tamper-evident chain are unchanged, so
 * the wording must not read as tampering ("altered") or as a deleted decision
 * ("erased"). See the Python docstring for the rejected alternatives.
 */
function formatIdentityErased(value) {
  return value ? 'Yes (data-protection erasure)' : 'No';
}

/**
 * `approval_export.format_decision_version` — F-12 / REQ-6102 (#592, D-05).
 * `v3`, or `v3 · restored at v7` when the decision reads fresh only because the
 * signed-off content came back. A STALE decision is not drift.
 */
function formatDecisionVersion(row, currentVersion) {
  const at = row && row.approved_at_version;
  if (at == null) return '';
  if (!row.stale && row.version_drift && currentVersion != null) {
    return `v${at} · restored at v${currentVersion}`;
  }
  return `v${at}`;
}

/** `approval_export._fmt_reviewers` — one auditable cell, qualifiers included. */
function formatReviewers(rows, currentVersion) {
  return (rows || []).map((r) => {
    const marks = [];
    if (r.stale) marks.push('stale');
    if (r.self_approved === true) marks.push('self-approved');
    const version = formatDecisionVersion(r, currentVersion);
    if (version.includes(' · ')) marks.push(`at ${version}`);
    // F-61 / REQ-6119 (#606, D-10): the decision's own wording, never "left org".
    if (r.reviewer_inactive === true) marks.push('user inactive');
    if (r.actor_erased) marks.push('identity pseudonymised');
    const who = r.actor || r.reviewer || 'unknown';
    return `${who} (${[r.state || '?', ...marks].join(', ')})`;
  }).join('; ');
}

/**
 * `{label: value}` for `APPROVAL_COLUMNS`, for ONE node of an `/export/json` subtree.
 * A node that was never annotated reports `not exported` rather than `pending` — the
 * backend draws that distinction and a client renderer that collapsed it would report
 * "nobody has approved this" for an export that simply never looked.
 */
export function approvalFields(node) {
  const record = node && node.approval;
  if (!record || typeof record !== 'object') {
    return Object.fromEntries(APPROVAL_COLUMNS.map((label) => [label, NOT_EXPORTED]));
  }
  const reviewers = record.reviewers || [];
  const state = String(record.state || NOT_EXPORTED);
  const standing = APPROVED_ROLLUP_STATES.includes(state);
  const currentVersion = record.current_version;
  return {
    'Approval State': state,
    'Approval Reviewers': formatReviewers(reviewers, currentVersion),
    'Approval Date': String(record.approved_at == null ? '' : record.approved_at),
    // Scoped to the STANDING approval, like "Approval Date" and "Self-Approved": a
    // superseded or lapsed decision's revision would misrepresent the record as current.
    'Approved At Version': standing ? formatDecisionVersion(record, currentVersion) : '',
    'Self-Approved': standing ? formatTristate(record.self_approved) : '',
    // NOT scoped to the standing approval: a rejection recorded by a reviewer who has
    // since lost access, or under a since-pseudonymised name, is as much of an audit
    // question as an approval is. Blank only when there is no decision to qualify.
    'Reviewer Inactive': reviewers.length ? formatTristate(record.reviewer_inactive) : '',
    'Identity Pseudonymised': reviewers.length
      ? formatIdentityErased(record.actor_erased) : '',
  };
}

/** Depth-first list of every node in a `{...,children:[]}` subtree. */
export function flattenSubtree(root) {
  const out = [];
  const walk = (node) => {
    if (!node) return;
    out.push(node);
    (node.children || []).forEach(walk);
  };
  walk(root);
  return out;
}

/** Attribute entries to display — the internal 'type' marker is skipped
 * (same rule as the backend Markdown exporter). */
export function attributeEntries(node) {
  return Object.entries((node && node.attributes) || {}).filter(([k]) => k !== 'type');
}

/** Attribute value → display string. Structured `{datatype, value}` attrs
 * render as their value; other objects as JSON. */
export function attributeValueText(value) {
  if (value && typeof value === 'object') {
    if ('value' in value) return attributeValueText(value.value);
    return JSON.stringify(value);
  }
  return value == null ? '' : String(value);
}

/** Human label for a node's item type: org type map first (type_id → label),
 * then the derived `type` key title-cased, then 'Item'. */
export function typeLabelFor(node, typeMap = {}) {
  if (node && node.type_id && typeMap[node.type_id]) return typeMap[node.type_id];
  const key = node && node.type;
  if (key) return String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return 'Item';
}

/** Safe-ish file base name from the document title (falls back to the id). */
export function exportFileBase(root, fallback = 'document') {
  const raw = (root && root.title) || fallback;
  const slug = String(raw).trim().toLowerCase()
    .replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
  return slug || 'document';
}

function csvCell(value) {
  const text = String(value == null ? '' : value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * Excel-compatible CSV of the whole subtree: ID (standardised display id),
 * Title, Type, Description (plain text), the approval block, then one column
 * per attribute key present anywhere in the subtree. Prefixed with a UTF-8 BOM
 * so Excel opens it with the right encoding.
 *
 * F-53 / REQ-6114 (#585): the approval columns are FIXED and unconditional, in the
 * same position the backend xlsx puts them (`xlsx_export._REQ_BASE_HEADERS` then
 * `_APPROVAL_HEADERS` then the attribute keys). Fixed rather than emitted-when-present
 * for the reason that exporter states: an auditor must be able to READ "pending", not
 * infer it from a missing column — and a spreadsheet whose columns shift depending on
 * whether anyone happened to approve anything cannot be diffed between exports.
 */
export function subtreeToCsv(root, { typeMap = {}, showHumanIds = true } = {}) {
  const nodes = flattenSubtree(root);
  const attrKeys = [];
  nodes.forEach((n) => attributeEntries(n).forEach(([k]) => {
    if (!attrKeys.includes(k)) attrKeys.push(k);
  }));
  const header = ['ID', 'Title', 'Type', 'Description', ...APPROVAL_COLUMNS,
                  ...VERIFICATION_COLUMNS, ...attrKeys];
  const rows = nodes.map((n) => {
    const attrs = (n && n.attributes) || {};
    const approval = approvalFields(n);
    const verification = verificationFields(n);
    return [
      resolveIdDisplay(n, showHumanIds).primary,
      n.title || '',
      typeLabelFor(n, typeMap),
      htmlToText(n.description || ''),
      ...APPROVAL_COLUMNS.map((label) => approval[label]),
      ...VERIFICATION_COLUMNS.map((label) => verification[label]),
      ...attrKeys.map((k) => (k in attrs ? attributeValueText(attrs[k]) : '')),
    ];
  });
  const body = [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
  return `\uFEFF${body}\r\n`;
}

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const PRINT_CSS = `
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
         color: #1a1a1a; margin: 32px; line-height: 1.5; }
  h1 { font-size: 20px; } h2 { font-size: 17px; } h3 { font-size: 15px; }
  h4, h5, h6 { font-size: 13px; }
  h1, h2, h3, h4, h5, h6 { margin: 14px 0 6px; }
  .idchip { font-family: ui-monospace, Menlo, monospace; font-size: 10px;
            border: 1px solid #bbb; border-radius: 4px; padding: 1px 5px;
            font-weight: 400; white-space: nowrap; vertical-align: middle; }
  .typechip { font-size: 9px; font-weight: 600; text-transform: uppercase;
              letter-spacing: .04em; border: 1px solid #bbb; border-radius: 4px;
              padding: 1px 5px; white-space: nowrap; vertical-align: middle; }
  p { margin: 4px 0 8px; white-space: pre-wrap; }
  table { border-collapse: collapse; width: 100%; margin: 6px 0 10px;
          font-size: 12px; table-layout: fixed; }
  th, td { border: 1px solid #ccc; padding: 3px 8px; text-align: left;
           vertical-align: top; word-break: break-word; }
  th { background: #f2f2f2; }
  th:first-child, td:first-child { width: 30%; }
  section { break-inside: avoid-page; }
  @media print { body { margin: 12mm; } }
`;

/**
 * A complete standalone HTML document (print stylesheet inlined) rendering
 * the subtree as a document — id chip + type chip on the title row, plain-text
 * description, approval/value table, attribute/value table — ready for
 * `window.print()` → PDF.
 *
 * F-53 / REQ-6114 (#585): the approval table is emitted for EVERY item, ahead of the
 * free-form attributes and unconditionally — the same shape and the same reasoning as
 * the backend's Word export (`docx_export._render_node`), which this is the closest
 * analogue of: a printed document is the artifact most likely to be read by a human
 * assessor, and "this item has no approval row" is itself the finding.
 */
export function buildPrintHtml(root, { typeMap = {}, showHumanIds = true } = {}) {
  const renderNode = (node, depth) => {
    const level = Math.min(depth + 1, 6);
    const ids = resolveIdDisplay(node, showHumanIds);
    const parts = [`<section>`];
    parts.push(
      `<h${level}>${escapeHtml(node.title || 'Untitled')} ` +
      `<span class="idchip">${escapeHtml(ids.primary)}</span> ` +
      `<span class="typechip">${escapeHtml(typeLabelFor(node, typeMap))}</span></h${level}>`
    );
    const description = htmlToText(node.description || '');
    if (description) parts.push(`<p>${escapeHtml(description)}</p>`);
    const approval = approvalFields(node);
    parts.push('<table><thead><tr><th>Approval</th><th>Value</th></tr></thead><tbody>');
    APPROVAL_COLUMNS.forEach((label) => parts.push(
      `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(approval[label])}</td></tr>`
    ));
    parts.push('</tbody></table>');
    const verification = verificationFields(node);
    parts.push('<table><thead><tr><th>Verification</th><th>Value</th></tr></thead><tbody>');
    VERIFICATION_COLUMNS.forEach((label) => parts.push(
      `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(verification[label])}</td></tr>`
    ));
    parts.push('</tbody></table>');
    const attrs = attributeEntries(node);
    if (attrs.length > 0) {
      parts.push('<table><thead><tr><th>Attribute</th><th>Value</th></tr></thead><tbody>');
      attrs.forEach(([k, v]) => parts.push(
        `<tr><td>${escapeHtml(k)}</td><td>${escapeHtml(attributeValueText(v))}</td></tr>`
      ));
      parts.push('</tbody></table>');
    }
    (node.children || []).forEach((child) => parts.push(renderNode(child, depth + 1)));
    parts.push('</section>');
    return parts.join('');
  };
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>` +
    `<title>${escapeHtml((root && root.title) || 'Document')}</title>` +
    `<style>${PRINT_CSS}</style></head><body>${renderNode(root, 0)}</body></html>`;
}

/**
 * Open a new window with the print-optimised document and invoke the browser
 * print dialog (users pick "Save as PDF"). Returns false when blocked (popup
 * blocker / non-browser env) so the caller can surface a message.
 */
export function openPrintWindow(html) {
  if (typeof window === 'undefined' || !window.open) return false;
  const win = window.open('', '_blank');
  if (!win || !win.document) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  // Give the new window a beat to lay out before the print dialog.
  setTimeout(() => {
    try { win.print(); } catch { /* window closed early — ignore */ }
  }, 250);
  return true;
}
